import React, { forwardRef, useCallback, useMemo, useState,useRef } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";
import { Form } from "antd";

const AcunetixPremiumMappingForm = forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [initialValues] = useState(() => {
    if (props.record) {
      const { targetId, targetGroupId } = props.record;
      if (targetGroupId) {
        return [
          {
            product: props.defaultProduct,
            subProduct: props.defaultSubProduct,
            environment: props.defaultEnvironment,
            mappingType: "targetGroups",
            targetGroupId,
            targetsByGroupIds: targetId ? targetId.split(",") : []
          }
        ];
      } else {
        return [
          {
            product: props.defaultProduct,
            subProduct: props.defaultSubProduct,
            environment: props.defaultEnvironment,
            mappingType: "targets",
            targetIds: targetId ? targetId.split(",") : []
          }
        ];
      }
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        environment: props.defaultEnvironment,
        mappingType: "targets"
      }
    ];
  });

  const targetNameMap = useRef({});
  const targetGroupNameMap = useRef({});

  const fetchTargets = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
        props.toolName, { login_id: props.loginConfigId, type: "targets", ...params });
        promise.then(res =>res.projects.forEach(p => {targetNameMap.current[p.id] = p.name; }));
        return { promise, cancel };
  }, [props.loginConfigId, props.toolName]);

  const fetchTargetGroups = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
        props.toolName,{ login_id: props.loginConfigId, type: "targetGroups", ...params });
        promise.then(res => {
        res.projects.forEach(p => {
          if (params.project) {
            targetGroupNameMap.current[p.id] = p.name;
          } else {
            targetGroupNameMap.current[p.id] = p.name;
          }
      });
    })
    return { promise, cancel };
  }, [props.loginConfigId, props.toolName]);

  const getExtraFieldsData = useCallback((config) => {
    const { mappingType, targetIds, targetGroupId, targetsByGroupIds } = config;   
    if (mappingType === "targets") {
      const names = (targetIds || [])
      .map(id => targetNameMap.current[id])
      .filter(Boolean)
      .join(",");
      return {
        targetId: targetIds?.join(","),
        mappingType,
        targetName : names
      };
    } 
    else {
      const groupName = targetGroupId? targetGroupNameMap.current[targetGroupId]: "";
      const names = (targetsByGroupIds || [])
      .map(id => targetNameMap.current[id])
      .filter(Boolean)
      .join(",");
      return {
        targetGroupId,
        targetId: targetsByGroupIds?.join(","),
        mappingType,
        targetGroupName : groupName,
        targetName : names
      };
    }
  }, []);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const {targetId, targetGroupId } = config;
    if (targetGroupId) {
      return {
        mappingType: "targetGroups",
        targetGroupId,
        targetsByGroupIds: targetId ? targetId.split(",") : []
      };
    }else {
      return {
        mappingType:"targets",
        targetIds: targetId.split(",")
      }};
  
  }, []);

  const extraFields = useMemo(() => [
    {
      name: 'mappingType',
      type: FieldTypes.RADIO,
      options: [
        { label: 'Targets', value: 'targets' },
        { label: 'Target Groups', value: 'targetGroups' }
      ],
    },
    {
      name: (listField) => [listField.name, "target_fields"],
      render: (listField) => {
        const configurations = form.getFieldsValue().configurations;
        let _fields = [];
        if (configurations?.[listField.name].mappingType === 'targets') {
          _fields = [{
            name: 'targetIds',
            colSpan: '12',
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Select Targets',
            required: true,
            props: {
              api: fetchTargets,
              mode: "multiple",
              allowClear: true,
              autoFocus: true
            }
          }]
        } else {
          _fields = [
            {
              name: 'targetGroupId',
              colSpan: '12',
              type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
              label: 'Select Target Group',
              required: true,
              props: {
                api: fetchTargetGroups,
                allowClear: true,
                autoFocus: true,
                onChange: (value) => {
                  const currentValues = form.getFieldsValue();
                  const updatedConfigurations = [...(currentValues.configurations || [])];
                  if (updatedConfigurations[0]) {
                    updatedConfigurations[0] = {
                      ...updatedConfigurations[0],
                      targetsByGroupIds: undefined
                    };
                    form.setFieldsValue({
                      configurations: updatedConfigurations
                    });
                  }
                }
              }
            },
            {
              name: 'targetsByGroupIds',
              colSpan: '12',
              type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
              label: 'Select Targets',
              required: true,
              props: {
                detechAPIParamChange: true,
                api: fetchTargetGroups,
                setExtraProps: (fieldProps, currentField) => {
                  const configurations = form.getFieldValue("configurations");
                  const currentConfiguration = configurations[currentField.name] || {};
                  const targetGroupId = currentConfiguration.targetGroupId;
                  const apiParams = { project: targetGroupId };

                  return {
                    apiParams,
                    resetOptionsProp: apiParams,
                    disabled: !targetGroupId,
                  }; 
                },
                mode: "multiple",
                allowClear: true
              }
            }
          ]
        }

        return generateFormFields(_fields, undefined, listField);
      },
    },
  ], [fetchTargets, fetchTargetGroups, form]);
  
  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props}
    />
  );
});

export default AcunetixPremiumMappingForm;